import axios, { AxiosRequestConfig } from "axios";

// Resolve once at module load. Mirrors the legacy `crudRequests.url` so the new
// modules talk to the same backend in every environment without bringing the
// legacy file along.
const inferBaseUrl = () => {
  if (typeof window === "undefined") return "https://server.brightlight.ai";
  if (window.location.href.includes("localhost")) return "http://localhost:3000";
  return "https://server.brightlight.ai";
};

export const API_BASE_URL = inferBaseUrl();

export type ApiAuth = {
  jwt: string | null | undefined;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;
  constructor(message: string, opts: { status: number; code?: string; body?: unknown }) {
    super(message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.body = opts.body;
  }
}

/**
 * Patient-app HTTP client. Always sends Bearer JWT + X-Tenant:patient and
 * withCredentials so cookie-based auth keeps working. The patient ID is
 * NEVER sent in the body — the server reads it from the verified token.
 */
export async function patientApiRequest<T = unknown>(
  auth: ApiAuth,
  config: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await axios({
      baseURL: API_BASE_URL,
      withCredentials: true,
      ...config,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.jwt ?? ""}`,
        "X-Tenant": "patient",
        ...(config.headers ?? {}),
      },
    });
    return response.data as T;
  } catch (err: any) {
    const status = Number(err?.response?.status ?? 0);
    const body = err?.response?.data;
    // Two shapes reach here: the refusal contract (`{code, error: sentence}`)
    // and the guardian routes' (`{error: {code, message}}`). Reading only the
    // first turned a guardian's refusal into "[object Object]" and lost its
    // code (memberships audit C33).
    const said = body && typeof body === "object" ? (body as any).error : undefined;
    const nested = said && typeof said === "object" ? said : null;
    const code =
      (body && typeof body === "object" ? (body as any).code : undefined) ??
      nested?.code;
    const message =
      (typeof said === "string" && said) ||
      (typeof nested?.message === "string" && nested.message) ||
      err?.message ||
      "Request failed";
    throw new ApiError(message, { status, code, body });
  }
}
